import { useState, useCallback } from 'react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { isTauri } from '@/lib/runtime-bridge';
import { desktopService } from '@/lib/desktop-service';
import { ControlDefinition } from '@/types/api';

interface SketchPersistenceOptions {
    mode: 'create' | 'view' | 'example';
    currentSketchId: string;
    editorCode: string;
    svgThumbnail: string | null;
    controls: ControlDefinition[];
    initialData?: {
        title?: string;
        description?: string;
        updatedAt?: string;
    };
}

export function useSketchPersistence({
    mode,
    currentSketchId,
    editorCode,
    svgThumbnail,
    controls,
    initialData,
}: SketchPersistenceOptions) {
    const { data: session } = useSession();
    const router = useRouter();

    const [sketchTitle, setSketchTitle] = useState(
        initialData?.title || (mode === 'create' ? 'Untitled Sketch' : '')
    );
    const [sketchDescription, setSketchDescription] = useState(
        initialData?.description || ''
    );
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(
        initialData?.updatedAt ? new Date(initialData.updatedAt) : null
    );

    const saveSketch = useCallback(async () => {
        if (isTauri()) {
            setIsSaving(true);
            try {
                const sketchToSave = {
                    id: currentSketchId,
                    title: sketchTitle,
                    description: sketchDescription,
                    code: editorCode,
                    svgThumbnail: svgThumbnail,
                    controls: controls,
                    updatedAt: new Date().toISOString()
                };

                await desktopService.saveSketch(sketchToSave);
                setLastSaved(new Date());
                alert('Sketch saved locally!');
            } catch (error) {
                console.error('Failed to save locally:', error);
                alert('Failed to save sketch locally. Make sure a sketches folder is selected in the homepage.');
            } finally {
                setIsSaving(false);
            }
            return;
        }

        if (!session) {
            alert('You must be logged in to save sketches');
            return;
        }

        setIsSaving(true);
        try {
            const endpoint = mode === 'create' || mode === 'example' ? '/api/sketches' : `/api/sketches/${currentSketchId}`;
            const method = mode === 'create' || mode === 'example' ? 'POST' : 'PUT';
            const response = await fetch(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    title: sketchTitle.replace(' (Example)', ''),
                    description: sketchDescription || undefined,
                    code: editorCode,
                    svgThumbnail: svgThumbnail,
                    isPublic: true,
                    controls: controls.length > 0 ? controls : undefined,
                }),
            });

            const data = await response.json();

            if (data.success) {
                setLastSaved(new Date());
                if (mode === 'create' || mode === 'example') {
                    router.push(`/id/${data.sketch.id}`);
                }
            } else {
                alert('Failed to save sketch: ' + data.error);
            }
        } catch (error) {
            console.error('Error saving sketch:', error);
            alert('Failed to save sketch');
        } finally {
            setIsSaving(false);
        }
    }, [session, sketchTitle, sketchDescription, editorCode, svgThumbnail, controls, router, mode, currentSketchId]);

    return {
        sketchTitle,
        setSketchTitle,
        sketchDescription,
        setSketchDescription,
        isSaving,
        lastSaved,
        setLastSaved,
        saveSketch,
        session,
    };
}
